/**
 * Who is signed in, and to which company.
 *
 * The console is a single-tenant view at any moment: a dispatcher works for one firm,
 * and every request carries that firm's token. Switching company means signing in
 * again, deliberately — a session that could silently change tenant is exactly the
 * hole the backend works so hard to keep shut.
 *
 * Nobody signs in to the platform here. That is the ops site, `web/ops`, on an address of
 * its own with accounts of its own; a firm's sign-in refuses an operator outright.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  ApiError,
  freshAccessToken,
  onSessionEnded,
  request,
  restore,
  setTenant,
  setTokens,
  type Schema,
} from '@taxi/api-client';
import { setLanguage } from '../i18n';
import { applyBrand, onSchemeChange } from '../theme';
import { AuthContext, type AuthState, type Branding } from './authContext';
import { sessionStore } from './sessionStore';
import { addressToMoveTo } from '../shell/address';

/**
 * What a sign-in comes back with: a session, or the demand for a second factor.
 *
 * The three token fields are the ones the endpoint always returned, so the shape is
 * additive — which is why nothing else in this app noticed MFA arriving.
 */
type AuthResult = Schema<'AuthResult'>;

/**
 * The refresh token survives a reload; the access token does not.
 *
 * Keeping only the refresh token in storage is the compromise the browser forces: a
 * dispatcher pressing F5 should not be signed out, but an access token sitting in
 * localStorage is readable by any script that gets onto the page. A stolen refresh
 * token is at least single-use and revokes its whole family when replayed. It lives in
 * `sessionStore`, which the client writes on every rotation.
 */
const TENANT_KEY = 'taxi.tenant';

/**
 * How often a signed-in console asks whether its access token is about to lapse.
 *
 * Asking costs nothing until it is — the refresh itself happens a minute before the
 * deadline. The point is the screen nobody touches: a desk showing the map makes no request
 * of its own for hours, so without this a session revoked at the server would go unnoticed
 * there until the socket next dropped, and then silently.
 */
const CHECK_EVERY_MS = 60_000;


export function AuthProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<Branding | null>(null);
  const [signedIn, setSignedIn] = useState(false);
  const [initialising, setInitialising] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const applyBranding = useCallback(async (next: Branding) => {
    setBranding(next);
    setTenant(next.slug);
    localStorage.setItem(TENANT_KEY, next.slug);

    // Branding is applied as CSS custom properties rather than inline styles, so a
    // tenant colour reaches every component without any of them knowing about tenants.
    // Through applyBrand rather than set directly, because a firm's accent has to be
    // legible on whichever ground is showing — a navy that works on #0e1318 is invisible
    // on #f4f6f8, and the same firm's dispatchers use both.
    applyBrand(next.primaryColor);
    document.title = `${next.displayName} — ${document.title.split('—').pop()?.trim() ?? ''}`;

    await setLanguage(next.defaultCulture);
  }, []);

  const resolveTenant = useCallback(
    async (slug: string) => {
      setTenant(slug);

      const fetched = await request<Branding>('/api/tenant/branding');
      await applyBranding(fetched);
    },
    [applyBranding],
  );

  /**
   * Which firm this address belongs to, if the address says.
   *
   * Asked with no company code attached at all, which is the whole trick: the API resolves
   * a firm from the host name before it looks at the `X-Tenant` header, so on
   * `varna.example.com` this answers and on plain `localhost` it does not. Returning the
   * slug rather than a boolean lets the caller notice that a stored session belongs to a
   * different firm than the one whose door this is.
   *
   * A dispatcher arriving at their own firm's address should not be asked to type its name:
   * the server already knows, and asking anyway is asking them to confirm the URL they just
   * opened. Somewhere without a firm in the address — the dev server on localhost, or a
   * shared host name — still asks, which is what the company-code step is for.
   */
  const resolveFromHost = useCallback(async (): Promise<string | null> => {
    setTenant(null);

    let fetched: Branding;

    try {
      fetched = await request<Branding>('/api/tenant/branding');
    }
    catch {
      // No firm answers on this address, which is not an error — it is the ordinary case
      // anywhere the address is not a firm's own.
      return null;
    }

    // On a name the firm does not list — its company-code address above all — the console
    // moves to the firm's own and goes no further here: nothing is signed in on a page that
    // is about to be replaced.
    const elsewhere = addressToMoveTo(window.location, fetched.addresses);

    if (elsewhere !== null) {
      window.location.replace(elsewhere);

      return new Promise<never>(() => undefined);
    }

    await applyBranding(fetched);

    return fetched.slug;
  }, [applyBranding]);

  /** Stores a session, whichever half of a sign-in produced it. */
  const keep = useCallback((session: { accessToken: string; refreshToken: string }) => {
    sessionStore.begin();
    setTokens({ ...session, expiresAt: new Date().toISOString() });
    setSessionExpired(false);
    setSignedIn(true);
  }, []);

  const completeSecondFactor = keep;

  const signIn = useCallback(
    async (email: string, password: string) => {
      const result = await request<AuthResult>('/api/auth/sign-in', {
        method: 'POST',
        body: { email, password },
      });

      if (result.mfa !== null) return result.mfa;

      keep({ accessToken: result.accessToken!, refreshToken: result.refreshToken! });

      return null;
    },
    [keep],
  );

  /**
   * Turns a link into an account with a password and a signed-in session.
   *
   * The firm is resolved first, and not only so the page can show its name and colour: the
   * API resolves a tenant in front of every endpoint under `/api`, so the invitation
   * endpoint cannot be reached without one. That is why the link carries the company code
   * beside the token.
   */
  const acceptInvitation = useCallback(
    async (slug: string, token: string, password: string) => {
      await resolveTenant(slug);

      const result = await request<AuthResult>(
        `/api/invitations/${encodeURIComponent(token)}/accept`,
        { method: 'POST', body: { password } },
      );

      // Redeeming an invitation is a first sign-in, so an administrator or a dispatcher is
      // challenged here exactly as they would be at the sign-in screen. That is what makes
      // enrolment mandatory rather than a nag they could postpone by never signing out.
      if (result.mfa !== null) return result.mfa;

      keep({ accessToken: result.accessToken!, refreshToken: result.refreshToken! });

      return null;
    },
    [keep, resolveTenant],
  );

  const signOut = useCallback(async () => {
    const refreshToken = await sessionStore.read();

    if (refreshToken !== null) {
      // Best effort. If the network is down the local session still ends, because
      // leaving a signed-in console on a shared dispatcher desk is the worse outcome.
      await request('/api/auth/sign-out', {
        method: 'POST',
        body: { refreshToken },
      }).catch(() => undefined);
    }

    setTokens(null);
    // After the request rather than before: a sign-out that had to refresh first, and was
    // refused, has already said the session expired — which somebody who pressed the
    // button does not need telling.
    setSessionExpired(false);
    setSignedIn(false);
  }, []);

  // Restore a session on load, so a reload does not interrupt a shift.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const slug = localStorage.getItem(TENANT_KEY);
      let stored = sessionStore.adopt();

      try {
        // The address first, the remembered company code second. The address is the
        // explicit statement of which firm this is — somebody who opened a firm's own URL
        // means that firm, whoever was signed in here last.
        const fromHost = await resolveFromHost();

        if (fromHost === null) {
          if (slug !== null) await resolveTenant(slug);
        }
        else if (slug !== null && slug !== fromHost) {
          // A session belonging to a different firm. Its refresh token would be refused at
          // this firm's door, so drop it here rather than letting the request fail and look
          // like an expired session.
          sessionStore.forget();
          stored = false;
        }

        // Through the client, under the same lock as every other refresh: a second tab
        // loading while the first rotates must present the token the first one left.
        // A refusal ends the session through the listener below, which is what says so.
        if (stored && (await restore()) === 'signed-in') {
          if (cancelled) return;

          setSignedIn(true);
        }
      } catch (error) {
        // A remembered firm that no longer answers is an ordinary end to a session, and
        // its token is no use to any other. Anything else — the API not up yet — keeps it
        // for the next reload, and is worth knowing about while developing.
        if (error instanceof ApiError) {
          sessionStore.forget();
        } else {
          console.error(error);
        }
      } finally {
        if (!cancelled) setInitialising(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolveTenant, resolveFromHost]);

  /**
   * Re-corrects the firm's accent when the scheme changes under it.
   *
   * The ground has moved, so the answer to "is this colour readable?" has moved with it.
   * Safe to re-run because `readableOn` is idempotent and this always starts from the
   * colour the API gave rather than from whatever was last stamped — otherwise a
   * dispatcher toggling light and dark a few times would watch the firm's colour walk away
   * from itself a step at a time.
   *
   * Runs immediately as well as on change, which is what covers the order the two arrive
   * in: the scheme is stamped before this bundle parsed, and the branding lands later.
   */
  useEffect(() => {
    if (branding === null) return undefined;

    return onSchemeChange(() => applyBrand(branding.primaryColor));
  }, [branding]);

  // The client ends the session when the server refuses to refresh it; the shell has to
  // follow, and the sign-in screen has to say why it is showing. Without the second half a
  // dispatcher who looked away for a minute comes back to a login form and no reason.
  useEffect(
    () =>
      onSessionEnded(() => {
        setSignedIn(false);
        setSessionExpired(true);
      }),
    [],
  );

  // Keeps the token fresh on a screen that makes no requests, and finds out soon when the
  // server has ended the session. Again the moment the tab is looked at or the network
  // comes back: a workstation waking from sleep has a token that lapsed hours ago, and the
  // live connection is about to reconnect with it.
  //
  // And at once when another tab signs out. Its access token would carry on working here
  // for up to fifteen minutes, so without this the tab somebody signed out of stays signed
  // in until its next refresh — a desk left open on a shared machine.
  useEffect(() => {
    if (!signedIn) return undefined;

    const check = () => void freshAccessToken();
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    const onStorage = (event: StorageEvent) => {
      if (!sessionStore.lost(event.key)) return;

      // This tab's own state only. The shared slot now belongs to the tab that changed it,
      // which may be signing somebody else in.
      setTokens(null);
      setSessionExpired(true);
      setSignedIn(false);
    };

    const timer = globalThis.setInterval(check, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', check);
    window.addEventListener('storage', onStorage);

    return () => {
      globalThis.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', check);
      window.removeEventListener('storage', onStorage);
    };
  }, [signedIn]);

  const value = useMemo<AuthState>(
    () => ({
      branding,
      signedIn,
      sessionExpired,
      initialising,
      resolveTenant,
      signIn,
      completeSecondFactor,
      acceptInvitation,
      signOut,
    }),
    [
      branding,
      signedIn,
      sessionExpired,
      initialising,
      resolveTenant,
      signIn,
      completeSecondFactor,
      acceptInvitation,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
